import { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { connect } from "react-redux";

import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import "chart.js/auto";

import constants from "../../assets/constants.js";
import * as BotpressAPI from "../../data/api/omniBotpressAPI.js";
import Widget from "../ui/widget.js";
import WidgetContent from "../ui/widgetContent.js";
import WidgetSkeleton from "../ui/widgetSkeleton.js";
import WidgetTitle from "../ui/widgetTitle.js";

ChartJS.register(ArcElement, Tooltip, Legend);

// Status values that aren't one of constants.assessmentStatus (null/error)
// render as "Unrated", matching the Assessments table.
const UNRATED_KEY = "unrated";

// Same colors as the status block in botpressAssessmentTable.js so the chart
// and the table read the same. 0 New, 1 Pass, 2 Fail, 3 Retest.
const getStatusColor = (theme, value) => {
  switch (value) {
    case 0:
      return theme.palette.highlight.main;
    case 1:
      return theme.palette.success.main;
    case 2:
      return theme.palette.error.main;
    case 3:
      return theme.palette.warning.main;
    default:
      return theme.palette.grey[500];
  }
};

// Pages through every assessment on the bot and tallies them by status. The
// Assessments widget only holds the current page, so the chart can't use that.
const fetchStatusCounts = async botODM => {
  const counts = {};
  let pageIndex = 0;
  let hasMore = true;
  const pageSize = 1000;

  while (hasMore) {
    const result = await BotpressAPI.searchAssessment({ botODM, pageIndex, pageSize });
    (result?.rows || []).forEach(row => {
      const isKnown = constants.assessmentStatus.some(item => item.value === row.status);
      const key = isKnown ? row.status : UNRATED_KEY;
      counts[key] = (counts[key] || 0) + 1;
    });
    hasMore = Boolean(result?.hasMore);
    pageIndex++;
  }

  return counts;
};

const BotpressAssessmentStatusGraph = ({
  testBotODM,
  assessment,
  widgetSize = "medium",
  minHeight = "333px",
  height,
}) => {
  const theme = useTheme();

  const [statusCounts, setStatusCounts] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Re-tally whenever the Assessments widget reloads its page (new runs,
  // status edits, deletes), so the chart follows the table.
  useEffect(() => {
    if (testBotODM == null) {
      setStatusCounts(null);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    fetchStatusCounts(testBotODM)
      .then(counts => {
        if (!cancelled) {
          setStatusCounts(counts);
        }
      })
      .catch(error => {
        console.error("Failed to load assessment status counts:", error);
        if (!cancelled) {
          setStatusCounts(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [testBotODM, assessment]);

  const total =
    statusCounts != null ? Object.values(statusCounts).reduce((sum, count) => sum + count, 0) : 0;

  const generateGraphData = () => {
    const labels = [];
    const data = [];
    const colors = [];

    const statusOptions = [...constants.assessmentStatus, { value: UNRATED_KEY, label: "Unrated" }];
    statusOptions.forEach(option => {
      const count = statusCounts[option.value] || 0;
      if (count === 0) {
        return;
      }
      labels.push(`${option.label}: ${count}`);
      data.push(count);
      colors.push(getStatusColor(theme, option.value));
    });

    return {
      labels,
      datasets: [
        {
          label: "Status",
          data,
          borderColor: colors,
          backgroundColor: colors,
          borderWidth: 1,
        },
      ],
    };
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "left",
        align: "top",
        labels: {
          font: {
            size: 24,
          },
        },
      },
      title: {
        display: false,
      },
    },
  };

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight} height={height}>
      <WidgetTitle title={"Assessment Status"} text={`${total} Assessments`} />

      {isLoading && statusCounts == null && <WidgetSkeleton />}
      {!(isLoading && statusCounts == null) && (
        <WidgetContent>
          {total > 0 ? (
            <Doughnut options={options} data={generateGraphData()} />
          ) : (
            <Box sx={{ position: "relative" }}>No data available</Box>
          )}
        </WidgetContent>
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  testBotODM: state.botpressSession.testBotODM,
  assessment: state.botpressSession.assessment,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(BotpressAssessmentStatusGraph);
